import {
  toMappingItem,
  type Mapping,
  type MappingItem,
} from '@pn/core/domain/data';
import { isDateString } from '@pn/core/domain/types';
import {
  cloneDeep,
  isArray,
  isBoolean,
  isNil,
  isNumber,
  isString,
  take,
} from 'lodash-es';
import type { GeoJsonDataItem } from './types';

const MAX_ITEMS_TO_ANALYZE = 1e3;

const PRESET_MAPPING_ITEMS: Record<string, MappingItem> = {
  internal_id: {
    source: 'geojson',
    field: 'internal_id',
    domainType: 'string',
    ui: {
      label: 'Internal ID',
      folder: 'Properties',
      isShownByDefault: true,
    },
  },
  geometry: {
    source: 'geojson',
    field: 'geometry',
    domainType: 'Geometry',
    ui: {
      label: 'N/A',
      folder: 'N/A',
      isNotRenderable: true,
    },
  },
};

export function generateMappingFromGeoJsonData(
  geoJsonData: GeoJsonDataItem[]
): Mapping {
  const mapping: Mapping = cloneDeep(PRESET_MAPPING_ITEMS);

  const geoJsonDataSnapshot = take(geoJsonData, MAX_ITEMS_TO_ANALYZE);

  getAllKeys(geoJsonDataSnapshot).forEach((key) => {
    if (!isNil(PRESET_MAPPING_ITEMS[key])) return;

    const mappingItem = generateMappingItem(geoJsonDataSnapshot, key);
    if (!isNil(mappingItem)) mapping[key] = mappingItem;
  });

  return mapping;
}

function getAllKeys(geoJsonDataSnapshot: GeoJsonDataItem[]): string[] {
  const keys = new Set<string>();

  geoJsonDataSnapshot.forEach((item) =>
    Object.keys(item).forEach((key) => keys.add(key))
  );

  return Array.from(keys);
}

function generateMappingItem(
  geoJsonData: GeoJsonDataItem[],
  key: string
): MappingItem | undefined {
  const result = iterateToDeduceDomainType(geoJsonData, key);
  if (isNil(result)) return undefined;

  return toMappingItem({
    domainType: result,
    source: 'geojson',
    field: key,
    ui: {
      label: key,
      folder: 'Properties',
      isShownByDefault: true,
    },
  });
}

type DeducedResult = 'DateString' | 'boolean' | 'number' | 'string' | undefined;

function iterateToDeduceDomainType(
  geoJsonData: GeoJsonDataItem[],
  key: string
): DeducedResult {
  for (const geoJsonDataItem of geoJsonData) {
    const result = deduceDomainType(geoJsonDataItem[key]);
    if (!isNil(result)) return result;
  }

  return undefined;
}

function deduceDomainType(value: unknown): DeducedResult {
  if (isArray(value))
    throw new Error(`Arrays are not supported. Value: ${value}`);
  if (isDateString(value)) return 'DateString';
  if (isBoolean(value)) return 'boolean';
  if (isNumber(value)) return 'number';
  if (isString(value)) return 'string';
  if (isNil(value)) return undefined;

  throw new Error(`Cannot deduce domain type for value: ${value}`); // should never happen
}
